import { useEffect } from 'react';
import { Check, Phone } from 'lucide-react';
import { FaqSection } from '../components/SEO';
import { ORGANIZATION_LD } from '../components/SEO';
import { PageHero } from '../components/PageShell';
import { PageShell } from '../components/PageShell';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { buildFaqLd } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const GolfPage = () => {
  const { t } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const seo = t.seo.golf;
  const faqs = t.golfPage.faqs || [];
  const heroImg = 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/uvwu077t_DSC06253.jpg';
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'SportsActivityLocation',
      '@id': `${SITE_URL}/golf#golf`,
      name: 'Golf Simulator – Padelarena Wädenswil',
      url: `${SITE_URL}/golf`,
      image: heroImg,
      description: seo.description,
      sport: 'Golf',
      address: ORGANIZATION_LD.address,
      telephone: '+41 43 833 0834',
      potentialAction: {
        '@type': 'ReserveAction',
        target: 'tel:+41438330834',
        result: { '@type': 'Reservation', name: 'Golf simulator booking (by phone)' }
      },
      parentOrganization: { '@id': `${SITE_URL}/#organization` }
    },
    faqs.length ? buildFaqLd(faqs) : null
  ].filter(Boolean);

  return (
    <PageShell testId="golf-page">
      <SEO path="/golf" title={seo.title} description={seo.description} keywords={seo.keywords} image={heroImg} jsonLd={jsonLd} />
      <PageHero
        label={t.golfPage.label}
        title={t.golfPage.titleStart}
        accent={t.golfPage.titleAccent}
        subtitle={t.golfPage.subtitle}
        image={heroImg}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.golfPage.aboutTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">{t.golfPage.about1}</p>
            <p className="text-slate-700 leading-relaxed">{t.golfPage.about2}</p>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.golfPage.featuresTitle}</h3>
            <ul className="space-y-2 text-slate-700">
              {t.golfPage.features.map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Golf photo gallery */}
          <div data-testid="golf-gallery">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.golfPage.galleryTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 aspect-[21/9] rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/88sug5tq_DSC06280.jpg" alt="Indoor golf simulator with driving range view at Padelarena Wädenswil" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="md:col-span-2 aspect-[16/10] rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/zsuza5ko_DSC06277.jpg" alt="Golf simulator booth with clubs and tee at Padelarena" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="aspect-[16/10] md:aspect-auto rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/yi0yerqe_DSC06291.jpg" alt="Golf ball on green turf — Padelarena indoor golf" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
          <div className="rounded-2xl p-6 bg-slate-900 text-white border border-slate-800" data-testid="golf-call-to-book">
            <p className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-3">{t.golfPage.callToBookTitle}</p>
            <p className="text-base md:text-lg text-slate-200 leading-snug mb-5">{t.golfPage.callToBookText}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:+41438330834"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid="golf-call-btn"
              >
                <Phone className="w-4 h-4" />
                +41 43 833 0834
              </a>
              <WhatsAppButton
                text={t.golfPage.callToBookWaText}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold border-2 border-white/30 text-white hover:bg-white/10 transition-colors"
                testId="golf-wa-btn"
              >
                {t.golfPage.callToBookWaCta}
              </WhatsAppButton>
            </div>
          </div>

          {faqs.length > 0 && (
            <FaqSection title={t.golfPage.faqTitle} faqs={faqs} />
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Pro Shop Page

export default GolfPage;
